import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/native';
import { AppIconButton, ICONS } from '@/components/base/app.icon.button';
import { Box } from '@/components/base/box';
import { Animated, Platform } from 'react-native';
import { AppPressable } from '@/components/base/app.pressable';
import { AppTextView } from '@/components/base/app.text.view';
import { ConferenceTypeEnum } from '@matap/shared';
import { useAppDispatch } from '@/hooks/use.redux';
import { actionShowAlert } from '@/redux/general.store';
import { P2PRoom } from '@/webrtc/p2p.room';

const MENU_ITEM_SIZE = 65;
const MENU_ITEM_MARGIN = 5;
const MENU_ITEM_DURATION = 300;
const MENU_WIDTH = 200;

interface Props {
  speakerOn?: boolean;
  muted?: boolean;
  onSwitchCamera?: () => void;
  onSpeaker?: () => void;
  onMute?: () => void;
  connection: P2PRoom;
}
function BaseCallButtonsView({ onSpeaker, onSwitchCamera, onMute, speakerOn, muted, connection }: Props) {
  return (
    <Container>
      {Platform.OS === 'web' && <DevicesMenu connection={connection} />}
      {onSwitchCamera && <IconButton icon={'switch_camera'} onPress={onSwitchCamera} />}
      {onSpeaker && (
        <AppIconButton icon={'speaker_enabled'} style={{ backgroundColor: speakerOn ? '#ffffff66' : undefined, borderRadius: 50, padding: 15 }} onPress={onSpeaker} />
      )}
      {onMute && (
        <AppIconButton size={'medium'} icon={'mute'} style={{ backgroundColor: muted ? '#ffffff66' : undefined, borderRadius: 50, padding: 15 }} onPress={onMute} />
      )}
    </Container>
  );
}

function DevicesMenu({ connection }: { connection: P2PRoom }) {
  const dispatch = useAppDispatch();
  const [expanded, setExpanded] = useState<'mic' | 'speaker' | 'camera'>();
  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
  const [speakerDevices, setSpeakerDevices] = useState<MediaDeviceInfo[]>([]);
  const [cameraDevices, setCameraDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedMic, setSelectedMic] = useState<string>();
  const [selectedSpeaker, setSelectedSpeaker] = useState<string>();
  const [selectedCamera, setSelectedCamera] = useState<string>();

  useEffect(() => {
    const mListener = () => {
      setMicDevices(connection.getDeviceManager().availableMicDevices);
      setSpeakerDevices(connection.getDeviceManager().availableSpeakerDevices);
      setCameraDevices(connection.getDeviceManager().availableCameraDevices);
      setSelectedSpeaker(connection.getDeviceManager().getSelectedSpeakerDevice());
      setSelectedMic(connection.getPeerLocalStream().getSelectedMic()?.getSettings().deviceId);
      setSelectedCamera(connection.getPeerLocalStream().getSelectedCamera()?.getSettings().deviceId);
    };

    connection.getPeerLocalStream().on('change', mListener);
    connection.getDeviceManager().on('change', mListener);

    mListener();

    return () => {
      connection.getDeviceManager().off('change', mListener);
      connection.getPeerLocalStream().off('change', mListener);
    };
  }, [connection]);

  return (
    <DevicesContainer flexDirection={'row'} alignItems={'center'} justifyContent={'space-evenly'}>
      <PickerMenu
        onSelect={(device) => {
          connection
            .getPeerLocalStream()
            .setMicDevice(device.deviceId)
            .then((res) => !res && dispatch(actionShowAlert('میکروفون انتخاب شده در دسترس نمیباشد')));
          setExpanded(undefined);
        }}
        selected={selectedMic}
        expanded={expanded === 'mic'}
        icon={'mic'}
        availableDevices={micDevices}
        setExpanded={(expanded) => setExpanded(expanded ? 'mic' : undefined)}
      />
      {'setSinkId' in HTMLAudioElement.prototype && (
        <PickerMenu
          onSelect={(device) => {
            connection.getPeerLocalStream().setSpeakerDevice(device.deviceId);
            setExpanded(undefined);
          }}
          selected={selectedSpeaker}
          expanded={expanded === 'speaker'}
          icon={'speaker'}
          availableDevices={speakerDevices}
          setExpanded={(expanded) => setExpanded(expanded ? 'speaker' : undefined)}
        />
      )}

      {connection.type === ConferenceTypeEnum.video && (
        <PickerMenu
          onSelect={(device) => {
            connection
              .getPeerLocalStream()
              .setCameraDevice(device.deviceId)
              .then((res) => !res && dispatch(actionShowAlert('دوربین انتخاب شده در دسترس نمیباشد')));

            setExpanded(undefined);
          }}
          selected={selectedCamera}
          expanded={expanded === 'camera'}
          icon={'camera_fill'}
          availableDevices={cameraDevices}
          setExpanded={(expanded) => setExpanded(expanded ? 'camera' : undefined)}
        />
      )}
    </DevicesContainer>
  );
}

function PickerMenu({
  icon,
  availableDevices,
  expanded,
  selected,
  setExpanded,
  onSelect,
}: {
  expanded: boolean;
  selected: string | undefined;
  icon: keyof typeof ICONS;
  availableDevices: MediaDeviceInfo[];
  onSelect: (device: MediaDeviceInfo) => void;
  setExpanded: (expanded: boolean) => void;
}) {
  const opacity = useMemo(() => new Animated.Value(0), []);
  const animates = useMemo(() => availableDevices.map(() => new Animated.Value(0)), [availableDevices]);

  useEffect(() => {
    if (expanded) {
      Animated.stagger(
        100,
        animates.map((a, index) =>
          Animated.timing(a, {
            toValue: (MENU_ITEM_SIZE + MENU_ITEM_MARGIN) * (index + 1) * -1,
            duration: MENU_ITEM_DURATION,
            useNativeDriver: false,
          }),
        ),
      ).start();

      Animated.timing(opacity, {
        toValue: 1,
        duration: MENU_ITEM_DURATION,
        useNativeDriver: false,
      }).start();
    } else {
      Animated.parallel(
        animates.map((a) =>
          Animated.timing(a, {
            toValue: 0,
            duration: MENU_ITEM_DURATION,
            useNativeDriver: false,
          }),
        ),
      ).start();

      Animated.timing(opacity, {
        toValue: 0,
        duration: MENU_ITEM_DURATION,
        useNativeDriver: false,
      }).start();
    }
  }, [expanded, animates, opacity]);

  return (
    <Box>
      {availableDevices.map((device, index) => {
        if (!device) return null;
        const animate = animates[index];
        const isSelected = selected === device.deviceId;
        return (
          <MenuItem key={device.deviceId} style={{ transform: [{ translateY: animate }], opacity }} selected={isSelected}>
            <PressableMenuItem onPress={() => onSelect(device)}>
              <AppTextView textAlign={'left'} textColor={isSelected ? 'white' : 'black_light'} text={device.label} />
            </PressableMenuItem>
          </MenuItem>
        );
      })}

      <MenuIconButton icon={icon} onPress={() => setExpanded(!expanded)} />
    </Box>
  );
}

const MenuItem = styled(Animated.View)<{ selected: boolean }>`
  position: absolute;
  width: ${MENU_WIDTH}px;
  height: ${MENU_ITEM_SIZE}px;
  background-color: ${({ theme, selected }) => (selected ? theme.colors.error : theme.colors.whiteFaded)};
  padding: 7px;
  border-color: ${({ theme }) => theme.colors.greyLight};
  border-width: 1px;
  border-radius: 7px;
`;

const IconButton = styled(AppIconButton)`
  padding: 15px;
  border-radius: 50px;
`;

const DevicesContainer = styled(Box)`
  border-color: white;
  border-radius: 15px;
  border-width: 1px;
`;

const PressableMenuItem = styled(AppPressable)`
  flex-grow: 1;
  align-items: center;
  justify-content: center;
`;

const MenuIconButton = styled(AppIconButton)`
  padding: 15px;
`;

const Container = styled(Box)`
  width: ${({ theme }) => theme.wp(100)}px;
  background-color: #01796c;
  flex-direction: row;
  align-items: center;
  z-index: 2;
  justify-content: space-evenly;
  padding-vertical: 5px;
`;

export const CallButtonsView = React.memo(BaseCallButtonsView);
